/**
 * Theme provider – persists the preference and applies `data-theme` to <html>.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ThemeContext, type ThemeContextValue, type ThemePreference } from './ThemeContext';

const THEME_STORAGE_KEY = 'bhg-theme-preference';

function getStoredPreference(): ThemePreference {
  if (typeof window === 'undefined') return 'system';
  const saved = window.localStorage.getItem(THEME_STORAGE_KEY) as ThemePreference | null;
  return saved === 'light' || saved === 'dark' || saved === 'system' ? saved : 'system';
}

function getSystemPrefersDark(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function applyTheme(isDark: boolean): void {
  document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(getStoredPreference);
  const [systemPrefersDark, setSystemPrefersDark] = useState(getSystemPrefersDark);

  const isDark = useMemo(
    () =>
      preference === 'dark' || (preference === 'system' && systemPrefersDark),
    [preference, systemPrefersDark]
  );

  useEffect(() => {
    applyTheme(isDark);
  }, [isDark]);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handle = () => setSystemPrefersDark(mq.matches);
    mq.addEventListener('change', handle);
    return () => mq.removeEventListener('change', handle);
  }, []);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    window.localStorage.setItem(THEME_STORAGE_KEY, next);
  }, []);

  const toggleTheme = useCallback(() => {
    const next = isDark ? 'light' : 'dark';
    setPreference(next);
  }, [isDark, setPreference]);

  const value = useMemo<ThemeContextValue>(
    () => ({ isDark, preference, setPreference, toggleTheme }),
    [isDark, preference, setPreference, toggleTheme]
  );

  return <ThemeContext value={value}>{children}</ThemeContext>;
}
